// Lib
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withHandlers, compose } from '../../../../../../../../node_module_clones/recompose';

// Utils
import { getPopupSelectorId } from './boardEditorPermissionUtils';
import { userIsRegistered } from '../../../../../../../../common/users/userHelper';
import { getElementId } from '../../../../../../../../common/elements/utils/elementPropertyUtils';

// Selectors
import boardEditorPermissionPopupSelector from './boardEditorPermissionPopupSelector';

// Actions
import { navigateToBoard } from '../../../../../../../reducers/navigationActions';
import { closePopup as closePopupAction } from '../../../../../../../components/popupPanel/popupActions';
import {
    removeUsersFromBoard,
    transferBoardOwnership,
    updateUserPermissionsForBoard,
} from '../../../../../../sharing/boardSharingActions';

// Components
import FloatingPanel from '../../../../../../../components/tooltips/FloatingPanel';
import PopupPanelMouseManager from '../../../../../../../components/popupPanel/PopupPanelMouseManager';
import BoardEditorPermissionPopupDescription from './BoardEditorPermissionPopupDescription';
import BoardEditorPermissionPopupActions from './BoardEditorPermissionPopupActions';

// Constants
import { TooltipPositions } from '../../../../../../../components/tooltips/tooltipConstants';
import { ACCESS_BITS, PERMISSION_VALUES } from '../../../../../../../../common/permissions/permissionConstants';

// Styles
import './BoardEditorPermissionPopup.scss';

const mapDispatchToProps = (dispatch, { popupId }) => ({
    closePopup: () => dispatch(closePopupAction(popupId, false)),
    dispatchRemoveSharedUser: (removeUserIds, boardId) =>
        dispatch(removeUsersFromBoard({ removeUserIds, boardId })).catch(() => null),
    dispatchNavigateToSharedBoard: (boardId) => dispatch(navigateToBoard({ boardId })),
    dispatchChangeUserSharingPermissions: (updateUserIds, boardId, permissions) =>
        dispatch(updateUserPermissionsForBoard({ updateUserIds, boardId, permissions })).catch(() => null),
    dispatchTransferBoardOwnership: (newOwnerId, boardId) => dispatch(transferBoardOwnership({ newOwnerId, boardId })),
});

const enhance = compose(
    connect(boardEditorPermissionPopupSelector, mapDispatchToProps),
    withHandlers({
        navigateToSharedBoard:
            ({ dispatchNavigateToSharedBoard, sharedBoard }) =>
            () =>
                dispatchNavigateToSharedBoard(getElementId(sharedBoard)),
        removeSharedUser:
            ({
                dispatchRemoveSharedUser,
                dispatchChangeUserSharingPermissions,
                hasPublicEditPermissions,
                sharedBoard,
                userAccess,
                closePopup,
            }) =>
            () => {
                closePopup();

                const shouldBlockUser = hasPublicEditPermissions && userIsRegistered(userAccess?.user);

                if (shouldBlockUser) {
                    return dispatchChangeUserSharingPermissions(
                        [userAccess.userId],
                        getElementId(sharedBoard),
                        ACCESS_BITS.BLOCKED,
                    );
                }

                dispatchRemoveSharedUser([userAccess.userId], [getElementId(sharedBoard)]);
            },
        transferBoardOwnership:
            ({ dispatchTransferBoardOwnership, userAccess, sharedBoard }) =>
            () =>
                dispatchTransferBoardOwnership(userAccess.userId, getElementId(sharedBoard)),
        setUserPermissionsFull:
            ({ dispatchChangeUserSharingPermissions, userAccess, sharedBoard }) =>
            () =>
                dispatchChangeUserSharingPermissions(
                    [userAccess.userId],
                    getElementId(sharedBoard),
                    PERMISSION_VALUES.FULL_ACCESS,
                ),
    }),
);

const BoardEditorPermissionPopup = ({
    popupOpen,
    userAccess,
    popupId,
    DescriptionComponent = BoardEditorPermissionPopupDescription,
    ActionsComponent = BoardEditorPermissionPopupActions,
    ...rest
}) => {
    if (!popupOpen || !userAccess) return null;

    return (
        <FloatingPanel
            selector={`.popup-trigger-${getPopupSelectorId({ user: userAccess.user })} .Icon`}
            className="BoardEditorPermissionPopup light"
            arrowWidth={13}
            arrowHeight={8}
            distance={15}
            offset={1}
            position={TooltipPositions.BOTTOM}
            pollPosition
        >
            <PopupPanelMouseManager
                {...rest}
                visible
                // Allow clicks outside this popup, but inside another popup, to close this popup
                ignoreClickContainment
                popupId={popupId}
            >
                <div className="popup-content">
                    <DescriptionComponent {...rest} {...userAccess} />
                    <ActionsComponent {...rest} {...userAccess} />
                </div>
            </PopupPanelMouseManager>
        </FloatingPanel>
    );
};

BoardEditorPermissionPopup.propTypes = {
    popupOpen: PropTypes.bool,
    popupId: PropTypes.string,
    userId: PropTypes.string,
    userAccess: PropTypes.object,
    DescriptionComponent: PropTypes.elementType,
    ActionsComponent: PropTypes.elementType,
    userAccessArray: PropTypes.array,

    dispatchTransferBoardOwnership: PropTypes.func,
    dispatchChangeUserSharingPermissions: PropTypes.func,
    setUserPermissionsFull: PropTypes.func,
};

export default enhance(BoardEditorPermissionPopup);
